import React, { useEffect, useState } from 'react';
import { Route, Routes, useNavigate, useParams } from 'react-router-dom';
import { useAccountStore } from '../../../components/Stores/accountStore';
import { useDemoModeStore } from '../../../components/Stores/demoModeStore';
import { useFutureOrderStore } from '../../../components/Stores/FutureOrdersStore';
import { useMaterialsStore } from '../../../components/Stores/MaterialsStore';
import { ChevronButton } from '../../../components/UI-Elements/Buttons/ChevronButton';
import { FormHeading } from '../../../components/UI-Elements/FormHeading';
import { LoadingSpinnerOverlay } from '../../../components/UI-Elements/LoadingSpinnerOverlay';
import { OverlayDisplayDiv } from '../../../components/UI-Elements/PopoverOverlays/OverlayDisplayDiv';
import { useExposure } from '../../../hooks/useExposure';
import { useSpecificFutureOrder } from '../../../hooks/useFutureOrders';
import { useAllProducts, useProduct } from '../../../hooks/useProducts';
import { CoCompleteOrder } from '../../ClientQuotes/ScreenComponents/ClientOrderPages/CompleteOrder/ClientOrderCompleteOrder';
import {
  ADD_FO_FIRST_STEP,
  ADD_FUTURE_ORDER_ROOT_ROUTE,
  FutureOrderPagesType,
  futureOrderTypeDictionary,
  goToNextPage,
  goToPrevPage,
  goToSummaryPage,
  onFutureOrderCreated,
  onModifyOrder,
  uploadNewFutureOrder,
} from './AddNewFutureOrderUtils';
import { FOB2BSummary } from './BackToBackFutureOrder/BackToBackFutureOrderSummery/FOB2BSummary';
import { FOB2BMaterialBreakdown } from './BackToBackFutureOrder/BackToBackModifyMaterialBreakdown/FOB2BMaterialBreakdown';
import { FOAttachFiles } from './CreateCustomerOrder/FOAttachFiles';
import { FOIndexedSummary } from './CreateOrderSummary/FOIndexedSummary';
import { FOModifyProductDeliveries } from './FillOrderDetails/FillOrderDetails';
import { FOIndexedClientExposure } from './FOIndexedClientExposure/FOIndexedClientExposure';
import { FutureOrderSummary } from './FutureOrderSummary/FutureOrderSummary';
import { FOIndexedSupplierExposure } from './RawMaterialsExposure/FOIndexedSupplierExposure';
import { FOSelectContractType } from './SelectContractType/NewFutureOrderSelectContractType';
import { IFutureOrder } from '../../../sp-types/FutureOrderType';
import { useUser } from '../../../hooks/useUser';
import { FormDialogBox } from './FillOrderDetails/OrderDetailsInputFields/OrderDetailsInputDialogBox';
import { StatusType } from '../../../sp-types/statusType';
import { FutureOrderSummaryRedirector } from './FutureOrderSummary/FutureOrderSummaryRedirector';
import { useCurrencyRates } from '@hooks/useCurrencyRates';

export const AddNewFutureOrder = () => {
  const { orderId: orderIdFromParams } = useParams();
  const { data: userData } = useUser();
  const [isLoading, setIsLoading] = useState(false);
  const [isExposureFetchLoading, setIsExposureFetchLoading] = useState(false);
  const { account } = useAccountStore();
  const { materialsMap } = useMaterialsStore();
  const { pricesMap } = useCurrencyRates();

  const { editedFutureOrder, updateFutureOrderProperty, resetStore, setEditedFutureOrder } = useFutureOrderStore();
  const { data: products, isLoading: isProductsLoading } = useAllProducts(account.id);
  const {
    data: currentProduct,
    refetch: refetchProduct,
    isLoading: isSingleProductLoading,
  } = useProduct(editedFutureOrder.productId);
  const { refetch: fetchExposure } = useExposure(account?.id || '');
  const {
    data: fetchedFutureOrder,

    isLoading: isFutureOrderLoading,
    refetch: refetchNewFutureOrderFromId,
  } = useSpecificFutureOrder(orderIdFromParams);
  const [isDisplayCompleted, setIsDisplayCompleted] = useState(false);
  const [isDisplayAdminSubmitPopup, setIsDisplayAdminSubmitPopup] = useState(false);
  const [isFutureOrderUpdatedFromParam, setIsFutureOrderUpdatedFromParam] = useState(false);

  useEffect(() => {
    refetchNewFutureOrderFromId();
  }, [orderIdFromParams]);

  useEffect(() => {
    return () => {
      resetStore(); // Reset the store when the component is unmounted
    };
  }, [resetStore]);

  useEffect(() => {
    editedFutureOrder.productId && refetchProduct();
  }, [editedFutureOrder.productId]);

  const { isDemoMode } = useDemoModeStore();
  const navigate = useNavigate();

  // The Real Problem lays here
  useEffect(() => {
    if (orderIdFromParams && fetchedFutureOrder) {
      if (fetchedFutureOrder.accountId && account?.id !== fetchedFutureOrder.accountId) {
        //security... order is not related to account
        console.debug('order is not from this account');
        navigate('/dashboard');
        return;
      }
      setIsFutureOrderUpdatedFromParam(!isFutureOrderUpdatedFromParam);
      setEditedFutureOrder(fetchedFutureOrder);
    }
  }, [orderIdFromParams, fetchedFutureOrder]);

  interface futureOrderScreen {
    mainScreen: React.ReactNode;
    formHeading?: { description: string; id: string; title: string };
    showSidebar: boolean;
    showSubmitBtn?: boolean;
  }

  const screenPageDictionary: Record<FutureOrderPagesType, futureOrderScreen> = {
    'select-contract-type': {
      mainScreen: (
        <FOSelectContractType
          nextButton={(value: string) => {
            const selectedContract = Object.keys(futureOrderTypeDictionary).find(
              (key) => futureOrderTypeDictionary[key].displayName === value,
            ) as any;
            updateFutureOrderProperty('futureOrderType', selectedContract);
            updateFutureOrderProperty('fileUrl', []);
            goToNextPage(getPageId(), selectedContract, navigate, orderIdFromParams);
          }}
        />
      ),
      formHeading: {
        description: 'Choose the type of end-client contract your looking into',
        title: 'Select contract type',
        id: '',
      },
      showSidebar: false,
    },
    'create-customer-order': {
      mainScreen: (
        <FOAttachFiles
          isDemoMode={isDemoMode}
          setNextScreen={() =>
            editedFutureOrder.futureOrderType &&
            goToNextPage(getPageId(), editedFutureOrder.futureOrderType, navigate, orderIdFromParams)
          }
          setSummaryScreen={(_orderUUID: string) =>
            goToSummaryPage(editedFutureOrder.futureOrderType || '', navigate, _orderUUID)
          }
          goBack={() => navigate(`/${ADD_FUTURE_ORDER_ROOT_ROUTE}/${orderIdFromParams}/select-contract-type`)}
        />
      ),
      formHeading: {
        description: 'Upload agreement or create a new order',
        title: 'Create future customer order',
        id: '',
      },
      showSidebar: false,
    },
    'fill-order-details': {
      mainScreen: (
        <FOModifyProductDeliveries
          accountId={account.id || ''}
          editedFutureOrder={editedFutureOrder}
          setEditedFutureOrder={setEditedFutureOrder}
          allProducts={products}
          backAction={() =>
            editedFutureOrder.futureOrderType &&
            goToPrevPage(getPageId(), editedFutureOrder.futureOrderType, navigate, orderIdFromParams)
          }
          nextAction={(newId?: string) =>
            editedFutureOrder.futureOrderType &&
            goToNextPage(getPageId(), editedFutureOrder.futureOrderType, navigate, newId ? newId : orderIdFromParams)
          }
        />
      ),
      formHeading: {
        description: 'Fill in and adjust order parameters',
        title: 'Modify product deliveries',
        id: '1.',
      },
      showSidebar: true,
    },
    'indexed-client-exposure': {
      mainScreen: (
        <FOIndexedClientExposure
          products={products || []}
          isFutureOrderGotUpdated={isFutureOrderUpdatedFromParam}
          materialsMap={materialsMap}
          backAction={() =>
            editedFutureOrder.futureOrderType &&
            goToPrevPage(getPageId(), editedFutureOrder.futureOrderType, navigate, orderIdFromParams)
          }
          nextAction={() =>
            editedFutureOrder.futureOrderType &&
            goToNextPage(getPageId(), editedFutureOrder.futureOrderType, navigate, orderIdFromParams)
          }
        />
      ),
      formHeading: {
        title: 'Modify material breakdown',
        description: 'Fill in and adjust order parameters ',
        id: '2/3',
      },
      showSidebar: true,
    },
    'indexed-supplier-exposure': {
      mainScreen: (
        <FOIndexedSupplierExposure
          products={products || []}
          backAction={() =>
            editedFutureOrder.futureOrderType &&
            goToPrevPage(getPageId(), editedFutureOrder.futureOrderType, navigate, orderIdFromParams)
          }
          nextAction={() =>
            editedFutureOrder.futureOrderType &&
            goToNextPage(getPageId(), editedFutureOrder.futureOrderType, navigate, orderIdFromParams)
          }
        />
      ),
      formHeading: {
        description: 'Fill in and adjust order parameters ',
        title: 'Modify materials supply price adjustment',
        id: '4.',
      },
      showSidebar: true,
    },
    'order-summary': {
      mainScreen: (
        <FutureOrderSummaryRedirector
          orderIdParam={orderIdFromParams}
          futureOrder={editedFutureOrder}
          onLoad={(orderType: string) => goToSummaryPage(orderType, navigate, orderIdFromParams)}
        />
      ),
      formHeading: { description: 'Review, refine and approve your order', title: 'Confirm your order', id: '' },
      showSidebar: true,
    },
    'indexed-order-summary': {
      mainScreen: (
        <FOIndexedSummary
          editedFutureOrder={editedFutureOrder}
          setEditedFutureOrder={setEditedFutureOrder}
          onModifyOrder={() =>
            onModifyOrder(
              editedFutureOrder,
              navigate,
              (_updatedFutureOrder: IFutureOrder) => setEditedFutureOrder(_updatedFutureOrder),
              account.id || '',
            )
          }
          products={products || []}
          routeToBackPage={() =>
            editedFutureOrder.futureOrderType &&
            goToPrevPage(getPageId(), editedFutureOrder.futureOrderType, navigate, orderIdFromParams)
          }
        />
      ),
      formHeading: {
        description: 'Review, refine and approve your order',
        title: 'Confirm your order',
        id: '',
      },
      showSidebar: true,
      showSubmitBtn: true,
    },
    'order-completed': {
      mainScreen: <CoCompleteOrder isLoading={isExposureFetchLoading} />,
      formHeading: {
        description: '',
        title: 'Complete your order',
        id: '',
      },
      showSidebar: false,
    },
    'back-to-back-summary': {
      mainScreen: (
        <FOB2BSummary
          products={products || []}
          currentFutureOrder={editedFutureOrder}
          setFutureOrderProperty={updateFutureOrderProperty}
          routeToModifyBackToBack={() =>
            onModifyOrder(
              editedFutureOrder,
              navigate,
              (_updatedFutureOrder: IFutureOrder) => setEditedFutureOrder(_updatedFutureOrder),
              account.id || '',
            )
          }
          routeToBackPage={() =>
            editedFutureOrder.futureOrderType &&
            goToPrevPage(getPageId(), editedFutureOrder.futureOrderType, navigate, orderIdFromParams)
          }
        />
      ),
      formHeading: {
        description: 'Review, refine and approve your order',
        title: 'Confirm your order',
        id: '',
      },
      showSubmitBtn: true,
      showSidebar: true,
    },
    'back-to-back-material-breakdown': {
      mainScreen: (
        <FOB2BMaterialBreakdown
          products={products || []}
          accountId={account.id || ''}
          currentFutureOrder={editedFutureOrder}
          setEditedFutureOrder={setEditedFutureOrder}
          updateFutureOrderProperty={updateFutureOrderProperty}
          backPage={() =>
            editedFutureOrder.futureOrderType &&
            goToPrevPage(getPageId(), editedFutureOrder.futureOrderType, navigate, orderIdFromParams)
          }
          nextPage={() =>
            editedFutureOrder.futureOrderType &&
            goToNextPage(getPageId(), editedFutureOrder.futureOrderType, navigate, orderIdFromParams)
          }
        />
      ),
      formHeading: {
        description: '',
        title: 'Modify material breakdown',
        id: '',
      },
      showSidebar: true,
    },
  };

  const showSideBar = (pageId: string): boolean =>
    screenPageDictionary[pageId as FutureOrderPagesType]
      ? screenPageDictionary[pageId as FutureOrderPagesType].showSidebar
      : true;

  const submitFutureOrder = async (futureOrderStatus: StatusType = 'completed') => {
    setIsLoading(true);
    const succeeded = await uploadNewFutureOrder(editedFutureOrder, account.id || '', futureOrderStatus);

    if (succeeded) {
      setIsDisplayCompleted(true);
      setIsExposureFetchLoading(true);

      await onFutureOrderCreated(account?.id || '', editedFutureOrder.totalExposure?.map((a) => a.materialId) || []);
      setTimeout(async () => {
        await fetchExposure();
        setIsExposureFetchLoading(false);
      }, 400);
    }
    setIsLoading(false);
  };

  return (
    <div className="flex flex-col h-full max-h-full flex-1 overflow-hidden ">
      {isDisplayAdminSubmitPopup && (
        <FormDialogBox
          handleCancel={() => {
            setIsDisplayAdminSubmitPopup(false);
            submitFutureOrder('underwriting');
          }}
          handleConfirm={() => {
            submitFutureOrder();
            setIsDisplayAdminSubmitPopup(false);
          }}
          isDialogOpen
          titleText="Select order status"
          descriptionText="Click Approve to submit the order, or Hold to wait for client approval."
          confirmButtonText="Approve order"
          cancelButtonText="Hold order"
        />
      )}
      {editedFutureOrder.status === 'completed' && (
        <div className="flex">
          <ChevronButton
            direction="left"
            onButtonClick={() => {
              navigate('/future-order');
            }}
          />
          <p className=" content-center text-lg ">Back to Future orders </p>
        </div>
      )}
      <div className="flex h-full  lex-1  px-2 box-border gap-6 relative overflow-auto ">
        {isLoading && isFutureOrderLoading && isProductsLoading && isSingleProductLoading && <LoadingSpinnerOverlay />}
        {isDisplayCompleted && (
          <OverlayDisplayDiv setDisplayOverlay={() => {}} hideClose={true}>
            {
              <div className="bg-white shadow-custom p-16">
                <CoCompleteOrder isLoading={isExposureFetchLoading} />
              </div>
            }
          </OverlayDisplayDiv>
        )}
        <div className="flex flex-col flex-1 bg-white shadow-custom overflow-auto p-3">
          <FormHeading
            status={(showSideBar(getPageId()) && editedFutureOrder.status) || undefined}
            description={
              screenPageDictionary[(getPageId() as FutureOrderPagesType) || 'CreateCustomerOrder'].formHeading
                ?.description
            }
            id={screenPageDictionary[(getPageId() as FutureOrderPagesType) || 'CreateCustomerOrder'].formHeading?.id}
            title={
              screenPageDictionary[(getPageId() as FutureOrderPagesType) || 'CreateCustomerOrder'].formHeading?.title
            }
          />
          {products ? (
            <Routes>
              {Object.entries(screenPageDictionary).map(([path, component]) => (
                <Route key={path} path={path} element={component.mainScreen} />
              ))}
            </Routes>
          ) : (
            <div> </div>
          )}
        </div>
        {showSideBar(getPageId()) && (
          <div className="w-[18%] overflow-y-auto">
            <FutureOrderSummary
              accountProducts={products}
              materialsMap={materialsMap}
              pricesMap={pricesMap}
              futureOrder={editedFutureOrder}
              product={editedFutureOrder.productId ? currentProduct : undefined}
              resetFutureOrderState={resetStore}
              submitFutureOrder={() => (userData?.isAdmin ? setIsDisplayAdminSubmitPopup(true) : submitFutureOrder())}
              showSubmit={
                screenPageDictionary[(getPageId() as FutureOrderPagesType) || 'CreateCustomerOrder'].showSubmitBtn
              }
            />
          </div>
        )}
      </div>
    </div>
  );
};

// [ToDo's for the future order]
// Arrange the code so that the modify future order code and the onSubmit will be on a separate files.
// Also make sure that all the functions are input and output based

const getPageId = (): FutureOrderPagesType =>
  (location.pathname.split('/').filter(Boolean).pop() as FutureOrderPagesType) || ADD_FO_FIRST_STEP;
